import React, { useState, useMemo } from 'react';

/**
 * CurrencyInput renders a Bootstrap input-group with a leading `$` symbol.
 * Users can toggle between entering a monthly or yearly amount.
 * The stored value is always **annual** — monthly input is multiplied by 12.
 *
 * @param {string}   label      - Visible field label.
 * @param {number|string} value - Current numeric value (annual, null/undefined → '').
 * @param {function} onChange   - Called with a float (annual) or null on change.
 * @param {string}   [hint]     - Optional helper text below the input.
 * @param {boolean}  [disabled]
 * @param {string}   [id]       - Input id (defaults to a slugified label).
 */
export default function CurrencyInput({
  label,
  value,
  onChange,
  hint,
  disabled = false,
  id,
  className,
  highlight = false,
}) {
  const inputId = id || label.toLowerCase().replace(/\s+/g, '_');
  const [isMonthly, setIsMonthly] = useState(false);

  // What the user sees in the input box
  const displayValue = useMemo(() => {
    if (value === null || value === undefined) return '';
    const annual = Number(value);
    if (isNaN(annual)) return '';
    if (!isMonthly) return String(annual);
    // Show monthly: round to 2 decimals to avoid floating-point noise
    const monthly = Math.round((annual / 12) * 100) / 100;
    // Drop trailing zeros for cleaner display
    return String(parseFloat(monthly.toFixed(2)));
  }, [value, isMonthly]);

  function handleChange(e) {
    const raw = e.target.value.replace(/[^0-9.]/g, '');
    if (raw === '' || raw === '.') {
      onChange(null);
    } else {
      const parsed = parseFloat(raw);
      if (isNaN(parsed)) {
        onChange(null);
      } else {
        // Always store as annual
        onChange(isMonthly ? Math.round(parsed * 12 * 100) / 100 : parsed);
      }
    }
  }

  function togglePeriod() {
    setIsMonthly((prev) => !prev);
  }

  return (
    <div className={className !== undefined ? className : 'mb-3'}>
      <label htmlFor={inputId} className={`form-label${highlight ? ' text-warning fw-semibold' : ''}`}>
        {label}
      </label>
      <div className={`input-group${highlight ? ' cashflow-highlight-ring' : ''}`}>
        <span className="input-group-text bg-light">$</span>
        <input
          id={inputId}
          type="text"
          inputMode="decimal"
          className="form-control"
          value={displayValue}
          onChange={handleChange}
          placeholder="0"
          disabled={disabled}
          autoComplete="off"
        />
        <button
          type="button"
          className={`input-group-text border ${isMonthly ? 'bg-primary text-white' : 'bg-light text-muted'}`}
          style={{ cursor: 'pointer', minWidth: '3rem', fontSize: '0.85rem', fontWeight: 500 }}
          onClick={togglePeriod}
          tabIndex={-1}
          title={isMonthly ? 'Currently entering monthly — click for yearly' : 'Currently entering yearly — click for monthly'}
        >
          /{isMonthly ? 'mo' : 'yr'}
        </button>
      </div>
      {isMonthly && value > 0 && (
        <div className="form-text text-primary">
          = ${Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/yr
        </div>
      )}
      {hint && <div className="form-text text-muted">{hint}</div>}
    </div>
  );
}
